import React, { useCallback } from 'react';

import { Text } from '@rbilabs/universal-components';
import { Image, ImageBackground, ImageProps } from 'expo-image';
import { View } from 'react-native';

import { useAppTakeover } from 'components/app-takeover/use-app-takeover';
import { Calories } from 'components/price-and-calories';
import { traceFirstMenuItemLoaded } from 'pages/menu/utils/performance';
import { t } from 'state/intl/translations';
import { primitive } from 'styles/constants/primitives';

import { menuSectionStyles } from '../menu-section/styled';

import {
  PriceAndCaloriesListWrapper,
  TileDescription,
  TileHeader,
  TileImage,
  TileImageWrapper,
} from './styled';
import { IMenuTileProps, IPriceAndCaloriesProps } from './types';

const ItemCalories = Text.withConfig({
  numberOfLines: 1,
  color: Styles.color.tertiary,
  marginTop: '1',
  marginBottom: '0',
  textTransform: 'capitalize',
});

const StyledItemPrice = Text.withConfig({
  fontWeight: 'bold',
  color: Styles.color.pricing,
  marginTop: '1',
  marginRight: {
    base: '1',
    md: '0',
  },
});

const priceAndCalories = ({
  calories,
  price,
  type,
}: IPriceAndCaloriesProps): JSX.Element | null => {
  const shouldRenderPriceAndCalories = price || calories;
  if (type === 'section' || !shouldRenderPriceAndCalories) {
    return null;
  }

  return (
    <PriceAndCaloriesListWrapper>
      <StyledItemPrice>{price}</StyledItemPrice>
      <Calories>
        <ItemCalories>{calories}</ItemCalories>
      </Calories>
    </PriceAndCaloriesListWrapper>
  );
};

// OLD legacy (not one menu)
/* NOTE: this component is used for main menu tiles and section tiles*/
const MenuTile = React.memo(
  ({
    image,
    objectFitContain,
    calories,
    name,
    type,
    price,
    id,
    accessibilityLabel = '',
    accessibilityLevel = 3,
    accessibilityRole = 'header',
  }: IMenuTileProps) => {
    const { menu: menuAppTakeover, pdp: pdpAppTakeover, fontFamily } = useAppTakeover();
    const takeoverSection = menuAppTakeover?.targetSectionIds?.[id];
    const takeoverItem = pdpAppTakeover?.targetMenuItemIds?.[id];
    const isTakeoverItem = !!takeoverItem;

    const priceAndCaloriesContent = priceAndCalories({ type, price, calories });

    const onImageDisplay = useCallback(() => {
      traceFirstMenuItemLoaded({ menuItemId: id, isOneMenu: 'false' });
    }, [id]);

    const Element = takeoverSection ? ImageBackground : View;

    return (
      <>
        <Element
          source={takeoverSection?.sectionBackgroundImage}
          style={[
            menuSectionStyles.menuSectionRow,
            {
              backgroundColor: isTakeoverItem
                ? takeoverSection?.itemBackgroundColor || takeoverItem?.sectionBackgroundColor
                : primitive.$white,
            },
          ]}
          accessibilityRole={accessibilityRole}
          accessibilityLevel={accessibilityLevel}
          accessibilityLabel={accessibilityLabel}
          dataSet={{ testid: 'menu-tile-bk', type, itemId: id }}
        >
          <TileImageWrapper>
            <TileImage
              image={image}
              alt={''}
              objectFitContain={objectFitContain}
              onDisplay={onImageDisplay}
              accessibilityHint={t('select')}
            />
          </TileImageWrapper>
          <TileDescription>
            <TileHeader
              testID={`tile-header-${name?.toLowerCase().replace(/\s/g, '')}`}
              fontFamily={isTakeoverItem ? fontFamily : undefined}
              maxWidth={takeoverSection?.titleMaxWidth}
            >
              {name}
            </TileHeader>
            {priceAndCaloriesContent}
          </TileDescription>
        </Element>
        {takeoverSection?.sectionImage && (
          <Image
            source={takeoverSection?.sectionImage}
            style={takeoverSection?.sectionImageStyle as ImageProps['style']}
          />
        )}
      </>
    );
  }
);

export default MenuTile;
